(() => {
  'use strict';
  const copy = window.chdCartCopy || {};
  const route = (name, fallback) => ((window.routes || {})[name] || `${window.Shopify?.routes?.root || '/'}${fallback}`).replace(/\.js$/, '');
  const cartRoute = () => route('cartUrl', 'cart');
  let queue = Promise.resolve();
  let pending = 0;
  let snapshot = null;
  let undoItem = null;
  let termsAccepted = false;
  let mobileObserver;
  let noteTimer;
  let lastNote = null;
  let lastError = null;
  let refreshing = null;
  let checkingOut = false;
  let needsReload = false;
  const cache = new Map();
  const message = (key) => copy[key] || key;

  const feedback = (text, options = {}) => {
    document.querySelectorAll('[data-chd-cart-feedback]').forEach((node) => {
      node.replaceChildren(document.createTextNode(text));
      node.hidden = !text;
      node.classList.toggle('is-error', Boolean(options.error));
      if (options.action) {
        const button = document.createElement('button');
        button.type = 'button'; button.textContent = options.label; button.dataset.chdFeedbackAction = options.action;
        node.append(' ', button);
      }
    });
  };
  const busy = () => {
    document.querySelectorAll('[data-chd-cart-form]').forEach((form) => {
      form.setAttribute('aria-busy', String(pending > 0));
      form.classList.toggle('is-updating', pending > 0);
    });
  };
  const serial = (task) => {
    pending += 1; busy();
    const result = queue.then(async () => { const value = await task(); lastError = null; return value; });
    queue = result.catch((error) => {
      lastError = error;
      if (error.saved) needsReload = true;
      document.querySelectorAll('[data-chd-cart-quantity]').forEach((input) => { input.value = input.dataset.currentValue; });
      feedback(error.message || message('error'), { error: true, action: 'reload', label: message('reload') });
    }).finally(() => { pending -= 1; busy(); });
    return queue;
  };
  const json = async (url, body) => {
    const request = body?.items ? window.chdStock.requestAdd : fetch;
    const response = await request(url, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      signal: AbortSignal.timeout(15000),
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new Error(message('error')); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : message('error'));
    return data;
  };
  const sectionIds = () => ['mini-cart', document.querySelector('[data-section-type="cart"]')?.dataset.sectionId].filter(Boolean);
  const sectionBody = () => ({ sections: sectionIds(), sections_url: cartRoute() });
  const rememberFocus = () => {
    const focus = document.activeElement;
    if (!focus?.closest('[data-chd-cart-form]')) return null;
    return { key: focus.closest('[data-chd-line-key]')?.dataset.chdLineKey, quantity: focus.matches('[data-chd-cart-quantity]'), delta: focus.dataset.chdQuantityDelta, id: focus.id };
  };
  const apply = (cart, sections) => {
    const focus = rememberFocus();
    const parser = new DOMParser();
    const replacements = [];
    const drawer = document.getElementById('mini-cart');
    const page = document.querySelector('[data-section-type="cart"]');
    const note = document.querySelector('[data-chd-cart-note]');
    const draft = note?.value;
    const noteWasOpen = note?.closest('details').open;
    for (const [id, target, selector] of [['mini-cart', drawer, '#mini-cart'], [page?.dataset.sectionId, page, '[data-section-type="cart"]']]) {
      if (!target) continue;
      const source = typeof sections?.[id] === 'string' && parser.parseFromString(sections[id], 'text/html').querySelector(selector);
      if (!source) throw new Error(message('error'));
      if (source.dataset.itemCount !== undefined && Number(source.dataset.itemCount) !== cart.item_count) throw new Error(message('error'));
      replacements.push({ target, source });
    }
    const scrollTop = drawer?.querySelector('.mini-cart__line-item-list')?.scrollTop;
    replacements.forEach(({ target, source }) => {
      target.innerHTML = source.innerHTML;
      target.dataset.itemCount = source.dataset.itemCount;
    });
    snapshot = cart;
    needsReload = false;
    window.theme.cartCount = cart.item_count;
    document.querySelectorAll('.header__cart-count').forEach((node) => { node.textContent = cart.item_count; });
    document.querySelectorAll('[data-chd-cart-terms]').forEach((input) => { input.checked = termsAccepted; });
    const newNote = document.querySelector('[data-chd-cart-note]');
    if (newNote && draft !== undefined) { newNote.value = draft; newNote.closest('details').open = noteWasOpen; }
    const list = drawer?.querySelector('.mini-cart__line-item-list');
    if (list && scrollTop != null) list.scrollTop = scrollTop;
    document.querySelector('.header')?.dispatchEvent(new CustomEvent('cart:rerendered', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('chd:cart:updated', { detail: { cart } }));
    enhance();
    if (focus) {
      const surface = drawer?.getAttribute('aria-hidden') === 'false' ? drawer : page;
      const row = surface && [...surface.querySelectorAll('[data-chd-line-key]')].find((node) => node.dataset.chdLineKey === focus.key);
      const next = focus.quantity ? row?.querySelector('[data-chd-cart-quantity]') : focus.delta ? row?.querySelector(`[data-chd-quantity-delta="${CSS.escape(focus.delta)}"]`) : document.getElementById(focus.id);
      (next || surface?.querySelector('[name="checkout"]') || surface?.querySelector('a'))?.focus({ preventScroll: true });
    }
  };
  const refreshNow = async () => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const [cart, sections] = await Promise.all([json(`${cartRoute()}.js`), json(`${cartRoute()}?sections=${sectionIds().map(encodeURIComponent).join(',')}`)]);
        if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error(message('error'));
        const parser = new DOMParser();
        for (const id of sectionIds()) {
          const selector = id === 'mini-cart' ? '#mini-cart' : '[data-section-type="cart"]';
          if (typeof sections?.[id] === 'string' && parser.parseFromString(sections[id], 'text/html').querySelector(selector)) continue;
          const response = await fetch(`${cartRoute()}?section_id=${encodeURIComponent(id)}`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(15000) });
          if (!response.ok || !sections || typeof sections !== 'object') throw new Error(message('error'));
          sections[id] = await response.text();
        }
        apply(cart, sections);
        return cart;
      } catch (error) {
        if (attempt === 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  };
  const refresh = () => {
    if (!refreshing) refreshing = serial(refreshNow).finally(() => { refreshing = null; });
    return refreshing;
  };
  const mutate = async (operation, payload) => {
    const result = await json(`${cartRoute()}/${operation}.js`, { ...payload, ...sectionBody() });
    const cart = operation === 'add' ? await json(`${cartRoute()}.js`) : result;
    if (result.sections && sectionIds().every((id) => typeof result.sections[id] === 'string')) {
      try { apply(cart, result.sections); } catch (_) {
        try { await refreshNow(); } catch (error) { error.saved = true; error.message = message('render_error'); throw error; }
      }
    }
    else {
      try { await refreshNow(); } catch (error) { error.saved = true; error.message = message('render_error'); throw error; }
    }
    return cart;
  };
  const change = (key, quantity) => serial(async () => {
    if (!Number.isInteger(quantity) || quantity < 0) throw new Error(message('invalid_quantity'));
    let removed;
    if (quantity === 0) {
      const cart = snapshot || await json(`${cartRoute()}.js`);
      const item = cart.items.find((line) => line.key === key);
      if (item) removed = { id: item.variant_id, quantity: item.quantity, properties: item.properties, ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}) };
    }
    await window.chdStock.run(async () => {
      if (quantity > 0) {
        const cart = await window.chdStock.readCart();
        const item = cart.items.find(line => line.key === key);
        if (!item) throw new Error(message('error'));
        const stock = await window.chdStock.readStock(item.variant_id);
        if (!window.chdStock.permitted(stock, cart.items, item.variant_id, quantity, key)) {
          await refreshNow();
          throw new Error(`${message('invalid_quantity')} ${stock.message}`);
        }
      }
      await mutate('change', { id: key, quantity });
    });
    if (removed) { undoItem = removed; feedback(message('removed'), { action: 'undo', label: message('undo') }); }
    else feedback(message('updated'));
  });
  const add = (item) => serial(async () => { await mutate('add', { items: [item] }); feedback(message('added')); });
  const saveNote = (value) => serial(async () => {
    await json(`${cartRoute()}/update.js`, { note: value }); lastNote = value;
  });

  const loadRecommendations = async (container) => {
    if (!container.hidden || container.dataset.loading) return;
    container.dataset.loading = 'true';
    const ids = container.dataset.productIds.split(',').filter(Boolean);
    try {
      const results = await Promise.all(ids.slice(0, 2).map((id) => {
        if (!cache.has(id)) cache.set(id, fetch(`${route('productRecommendationsUrl', 'recommendations/products')}?section_id=chd-cart-recommendations&product_id=${encodeURIComponent(id)}&intent=complementary&limit=6`).then((r) => r.ok ? r.text() : ''));
        return cache.get(id);
      }));
      if (!container.isConnected) return;
      const seen = new Set(ids);
      const offers = [];
      results.forEach((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-chd-offer-card]').forEach((card) => {
          if (!seen.has(card.dataset.chdOfferProduct) && offers.length < Number(container.dataset.limit)) { seen.add(card.dataset.chdOfferProduct); offers.push(card); }
        });
      });
      if (offers.length) { container.querySelector('[data-chd-offer-list]').replaceChildren(...offers); container.hidden = false; }
    } catch (_) { container.dataset.loading = ''; }
  };
  const enhance = () => {
    busy();
    const drawer = document.getElementById('mini-cart');
    if (drawer && !document.querySelector('.chd-cart-backdrop')) {
      const backdrop = document.createElement('button'); backdrop.type = 'button'; backdrop.className = 'chd-cart-backdrop'; backdrop.tabIndex = -1; backdrop.setAttribute('aria-hidden', 'true'); backdrop.dataset.action = 'toggle-mini-cart'; drawer.before(backdrop);
    }
    document.querySelectorAll('[data-chd-cart-offers]').forEach((container) => {
      if (!container.closest('#mini-cart') || drawer?.getAttribute('aria-hidden') === 'false') loadRecommendations(container);
    });
    const bar = document.querySelector('[data-chd-cart-mobile-bar]');
    const summary = document.querySelector('.chd-cart-page__summary');
    mobileObserver?.disconnect();
    if (bar && summary) {
      mobileObserver = new IntersectionObserver(([entry]) => { bar.hidden = entry.isIntersecting; }, { threshold: 0 });
      mobileObserver.observe(summary);
    }
    const note = document.querySelector('[data-chd-cart-note]');
    if (lastNote === null && note) lastNote = note.value;
  };
  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-chd-quantity-delta], [data-chd-cart-remove], [data-chd-offer-add], [data-chd-feedback-action], [data-chd-scroll-checkout]');
    if (!target) return;
    event.preventDefault();
    if (pending) return;
    if (target.hasAttribute('data-chd-scroll-checkout')) {
      const summary = document.querySelector('.chd-cart-page__summary'); summary?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      summary?.querySelector('[data-chd-cart-terms], [name="checkout"]')?.focus({ preventScroll: true }); return;
    }
    if (target.dataset.chdFeedbackAction === 'reload') { location.assign(cartRoute()); return; }
    if (needsReload) { feedback(message('render_error'), { error: true, action: 'reload', label: message('reload') }); return; }
    if (target.dataset.chdFeedbackAction === 'undo' && undoItem) {
      const item = undoItem; undoItem = null;
      serial(async () => { try { await mutate('add', { items: [item] }); feedback(message('restored')); } catch (error) { undoItem = item; throw error; } }); return;
    }
    if (target.hasAttribute('data-chd-offer-add')) {
      const select = target.closest('[data-chd-offer-card]').querySelector('[data-chd-offer-variant]');
      const variant = select ? select.value : target.dataset.variantId;
      if (!variant) { select?.focus(); feedback(message('choose_variant')); return; }
      add({ id: Number(variant), quantity: Number(target.dataset.quantity) || 1 }); return;
    }
    const row = target.closest('[data-chd-line-key]');
    const input = row?.querySelector('[data-chd-cart-quantity]');
    if (target.hasAttribute('data-chd-cart-remove')) { change(row.dataset.chdLineKey, 0); return; }
    const quantity = Number(input.value) + Number(target.dataset.chdQuantityDelta);
    if (quantity > 0 && quantity < Number(input.min)) { change(row.dataset.chdLineKey, 0); return; }
    if (input.max && quantity > Number(input.max)) { feedback(message('invalid_quantity'), { error: true }); return; }
    change(row.dataset.chdLineKey, Math.max(0, quantity));
  });
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-chd-cart-terms]')) {
      termsAccepted = input.checked;
      document.querySelectorAll('[data-chd-cart-terms]').forEach((node) => { node.checked = termsAccepted; }); return;
    }
    if (input.matches('[data-chd-cart-quantity]')) {
      if (input.value === input.dataset.currentValue) return;
      if (needsReload) { input.value = input.dataset.currentValue; feedback(message('render_error'), { error: true, action: 'reload', label: message('reload') }); return; }
      if (pending || !input.checkValidity() || !Number.isInteger(Number(input.value))) { input.reportValidity(); input.value = input.dataset.currentValue; return; }
      change(input.closest('[data-chd-line-key]').dataset.chdLineKey, Number(input.value));
    }
    if (input.matches('[data-chd-offer-variant]')) {
      const card = input.closest('[data-chd-offer-card]');
      const selected = input.selectedOptions[0];
      if (selected.dataset.price) card.querySelector('[data-chd-offer-price]').textContent = selected.dataset.price;
      card.querySelector('[data-chd-offer-add]').dataset.quantity = selected.dataset.min || '1';
      card.querySelector('[data-chd-offer-add]').textContent = input.value ? message('add_to_cart') : message('choose_variant');
    }
  });
  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-chd-cart-note]')) return;
    clearTimeout(noteTimer); noteTimer = setTimeout(() => saveNote(event.target.value), 600);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.matches('[data-chd-cart-quantity]')) { event.preventDefault(); event.target.dispatchEvent(new Event('change', { bubbles: true })); }
    const drawer = document.getElementById('mini-cart');
    if (event.key === 'Tab' && drawer?.getAttribute('aria-hidden') === 'false') {
      const nodes = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]),select,textarea')].filter((node) => node.tabIndex >= 0 && node.getClientRects().length);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('[data-chd-cart-form]') || event.submitter?.name === 'update') return;
    event.preventDefault();
    const terms = form.querySelector('[data-chd-cart-terms]');
    if (terms && !terms.checked) { terms.reportValidity(); terms.focus(); return; }
    if (checkingOut) return;
    checkingOut = true;
    form.dataset.submitting = 'true';
    clearTimeout(noteTimer);
    const note = form.querySelector('[data-chd-cart-note]')?.value;
    try {
      if (pending) feedback(message('network_pending'));
      await queue;
      if (lastError) throw lastError;
      if (note !== undefined && note !== lastNote) await json(`${cartRoute()}/update.js`, { note });
      const liveForm = form.id ? document.getElementById(form.id) : form;
      if (!liveForm || liveForm.querySelector('[data-chd-cart-terms]:not(:checked)')) return;
      const checkout = document.createElement('input'); checkout.type = 'hidden'; checkout.name = 'checkout'; checkout.value = '1'; liveForm.appendChild(checkout);
      HTMLFormElement.prototype.submit.call(liveForm);
    } catch (error) { feedback(error.message || message('error'), { error: true, action: 'reload', label: message('reload') }); }
    finally { checkingOut = false; delete form.dataset.submitting; }
  }, true);
  window.chdCart = { refresh, enhance };
  document.addEventListener('chd:cart:opened', enhance);
  window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhance, { once: true }); else enhance();
})();
